import * as prismicH from '@prismicio/helpers'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { createClient, predicate } from 'services/prismic'

import { isBlank, isPresent } from 'utils'

import ArticleCard from 'components/ArticleCard'
import Button from 'components/Button'
import Divider from 'components/Divider'
import Header from 'components/Header'
import Pagination from 'components/Pagination'
import PrismicPage from 'components/PrismicPage'
import RadioInputs from 'components/RadioInputs'
import SmallScreenModal from 'components/SmallScreenModal'
import Spacer from 'components/Spacer'
import SubHeader from 'components/SubHeader'
import Tags from 'components/Tags'
import Text from 'components/Text'

import {
  Articles,
  Column,
  Columns,
  FeaturedArticles,
  Filter,
  List,
  LookBooksAndGuides,
  Main,
  PopularTags,
} from './index.style'

const prismicArticleToArticleCardProps = ({ uid, data }) => {
  return {
    layout: 'vertical',
    handle: uid,
    imageUrl: data.cover_image?.url,
    category: data.category?.data?.title,
    title: data.title,
    excerpt: prismicH.asHTML(data.summary),
  }
}

const prismicTagsToTagsProps = (tags) => {
  return tags
    .filter(({ tag }) => !tag.isBroken && tag.data)
    .map(({ tag: { data } }) => ({
      imageUrl: data.image?.url,
      tag: data.tag,
    }))
}

export default function Index({
  pageDocument,
  articles,
  featuredArticles,
  lookbookArticles,
  guideArticles,
  popularTags,
  categoryOptions,
}) {
  const router = useRouter()

  // const handlePageChange = (page) => {
  //   const path = '/journal'
  //   const query = page > 1 ? `?page=${page}` : ''

  //   router.push(`${path}${query}`)
  // }

  const [categoryOption, setCategoryOption] = useState(router.query.category || null)

  const handleCategoryChange = (category) => {
    setCategoryOption(category)
    const query = {}

    if (isPresent(category)) query.category = category

    router.push({
      pathname: '/journal',
      query,
    })
  }

  return (
    <>
      {isBlank(categoryOption) && pageDocument && <PrismicPage document={pageDocument} />}
      <Main>
        <Filter>
          <SmallScreenModal toggleText="Filter">
            <SubHeader>Filter</SubHeader>
            <Spacer y={2} />
            <Text weight="bold">Category</Text>
            <Spacer y={1} />
            <RadioInputs value={categoryOption} options={categoryOptions} onChange={handleCategoryChange} />
          </SmallScreenModal>
        </Filter>
        <Articles>
          <Columns>
            {articles.results.map(prismicArticleToArticleCardProps).map((props, index) => (
              <Column key={index}>
                <ArticleCard {...props} />
              </Column>
            ))}
          </Columns>
          <Pagination page={articles.page} totalPages={articles.total_pages} viewAllEnabled={false} />
        </Articles>
      </Main>
      {(isPresent(lookbookArticles) || isPresent(guideArticles)) && (
        <LookBooksAndGuides>
          <div>
            <div className="flex justify-between items-end flex-wrap">
              <Header>Lookbooks</Header>
              <Link href="/journal/sections/lookbook" passHref legacyBehavior>
                <Button as="a" text="View more" variant="inline" />
              </Link>
            </div>
            <Spacer y={8} />
            <List>
              {lookbookArticles.results.map(prismicArticleToArticleCardProps).map((props, index) => (
                <Column key={index}>
                  <ArticleCard {...props} />
                </Column>
              ))}
            </List>
          </div>
          <div>
            <div className="flex justify-between items-end flex-wrap">
              <Header>Guides</Header>
              <Link href="/journal/sections/guide" passHref legacyBehavior>
                <Button as="a" text="View more" variant="inline" />
              </Link>
            </div>
            <Spacer y={8} />
            <List>
              {guideArticles.results.map(prismicArticleToArticleCardProps).map((props, index) => (
                <Column key={index}>
                  <ArticleCard {...props} layout="horizontal" />
                </Column>
              ))}
            </List>
          </div>
        </LookBooksAndGuides>
      )}
      {isPresent(featuredArticles) && (
        <FeaturedArticles>
          <Header>Featured Articles</Header>
          <Spacer y={8} />
          <Columns>
            {featuredArticles.results.map(prismicArticleToArticleCardProps).map((props, index) => (
              <Column key={index}>
                <ArticleCard {...props} />
              </Column>
            ))}
          </Columns>
        </FeaturedArticles>
      )}
      {isPresent(popularTags) && (
        <PopularTags>
          <Header>Popular Tags</Header>
          <Tags tags={prismicTagsToTagsProps(popularTags)} />
        </PopularTags>
      )}
      <Divider />
    </>
  )
}

export async function getServerSideProps({ previewData, query }) {
  const prismic = createClient({ previewData })
  const page = isPresent(query.page) ? parseInt(query.page) : 1
  const category = isPresent(query.category) ? query.category : null

  const categories = await prismic.getByType('article_category', {
    orderings: {
      field: 'my.article_category.title',
      direction: 'asc',
    },
  })

  const categoryOptions = [
    { value: null, label: 'All' },
    ...categories.results.map(({ uid, data: { title } }) => ({ value: uid, label: title })),
  ]

  const articlesPredicates = []

  if (isPresent(category)) {
    const categoryId = categories.results.find(({ uid }) => uid === category)?.id
    articlesPredicates.push(predicate.at('my.article.category', categoryId))
  }

  const articles = await prismic.getByType('article', {
    predicates: articlesPredicates,
    fetch: ['article.published_at', 'article.title', 'article.cover_image', 'article.category', 'article.summary'],
    fetchLinks: 'article_category.title',
    orderings: {
      field: 'my.article.published_at',
      direction: 'desc',
    },
    pageSize: 9,
    page,
  })

  let pageDocument = null
  let featuredArticles = null
  let lookbookArticles = null
  let guideArticles = null
  let popularTags = null

  if (page === 1) {
    pageDocument = await prismic.getPage('journal')
    featuredArticles = await prismic.get({
      predicates: [predicate.at('document.type', 'article'), predicate.at('my.article.featured', 'Yes')],
      fetch: ['article.title', 'article.cover_image', 'article.category'],
      fetchLinks: 'article_category.title',
      pageSize: 3,
    })
    lookbookArticles = await prismic.get({
      predicates: [predicate.at('document.type', 'article'), predicate.at('my.article.category', 'XKYPcBAAADSvf7TA')],
      fetch: ['article.title', 'article.cover_image', 'article.category'],
      fetchLinks: 'article_category.title',
      pageSize: 2,
    })
    guideArticles = await prismic.get({
      predicates: [predicate.at('document.type', 'article'), predicate.at('my.article.category', 'XKYPURAAADSvf7Qx')],
      fetch: ['article.title', 'article.cover_image'],
      pageSize: 6,
    })
    popularTags = (
      await prismic.getSingle('popular_tags', {
        graphQuery: `{
        popular_tags {
          tags {
            tag {
              tag
              image
            }
          }
        }
      }`,
      })
    ).data.tags
  }

  return {
    props: {
      pageDocument,
      articles,
      featuredArticles,
      lookbookArticles,
      guideArticles,
      popularTags,
      categoryOptions,
    },
  }
}
